"use client";

import { Button } from "@/components/shared/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/shared/ui/sheet";
import { GTM } from "@/lib/analytics/analytics-index";
import { useCartStore } from "@/state-management/stores/cart-store";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { memo, useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { findOrCreateCartAndSetCookie } from "../../../lib/cart/cart-actions";
import TransactionsListWithHeader from "../cart-edit/transactions-list-with-header";
import { CartHeader } from "./cart-header";
import OpenCart from "./open-cart";

const EmptyCartMessage = memo(function EmptyCartMessage() {
    return <p className="mt-6 text-center text-2xl font-bold">Your cart is empty.</p>;
});

const CartTotal = memo(function CartTotal({ total }: { total: number }) {
    const formattedTotal = useMemo(() => {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "USD",
            currencyDisplay: "narrowSymbol"
        }).format(total);
    }, [total]);

    return (
        <div className="mb-3 flex items-center justify-between border-t border-neutral-200 pb-1 pt-3 dark:border-neutral-700">
            <p>Total</p>
            <p>{formattedTotal}</p>
        </div>
    );
});

export default function CartModal() {
    const { cart, skuCount } = useCartStore(
        useShallow((state) => ({
            cart: state.cart,
            skuCount: state.cart ? new Set(state.cart.transactions.map((t) => t.productID)).size : 0
        }))
    );

    useEffect(() => {
        if (!cart) {
            findOrCreateCartAndSetCookie();
        }
    }, [cart]);

    const { data: session } = useSession();

    return (
        <Sheet>
            <SheetTrigger asChild>
                <button aria-label="Open cart">
                    <OpenCart quantity={skuCount} />
                </button>
            </SheetTrigger>
            <SheetTitle hidden>Cart</SheetTitle>
            <SheetContent
                side="right"
                className="w-full border-l border-neutral-200 p-0 dark:border-neutral-700 md:w-1/2 md:max-w-[800px] [&>button:first-child]:hidden"
                aria-describedby={undefined}
            >
                <div className="flex h-full flex-col">
                    <div className="p-6">
                        <CartHeader
                            customerName={cart?.customerName ?? ""}
                            customerID={cart?.customerID ?? 0}
                            cartID={cart?.cartID ?? 0}
                            enableMock={session?.user.role !== "Customer" ?? false}
                            mockDefaultValues={{
                                PONum: cart?.POnum,
                                deliveryDate: cart?.requestedDate
                                    ? { date: cart?.requestedDate, available: true }
                                    : undefined,
                                orderMemo: cart?.memo
                            }}
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {!cart || cart.transactions.length === 0 ? (
                            <EmptyCartMessage />
                        ) : (
                            <TransactionsListWithHeader
                                transactions={cart.transactions}
                                productPromotions={cart.promotionsProducts || null}
                            />
                        )}
                    </div>

                    <div className="bg-white px-6 pb-5 dark:bg-black">
                        <CartTotal total={cart?.totalExtPrice ?? 0} />
                        <SheetTrigger asChild>
                            <Link href="/checkout">
                                <Button
                                    className="block w-full p-3 text-center text-sm font-medium text-white opacity-90 hover:opacity-100"
                                    type="button"
                                    onClick={() =>
                                        GTM.sendEvent({
                                            event: "begin_checkout",
                                            ecommerce: {
                                                currency: "USD",
                                                value: cart?.totalExtPrice || 0,
                                                items: GTM.itemsFromCart(cart?.transactions || [])
                                            }
                                        })
                                    }
                                >
                                    Checkout
                                </Button>
                            </Link>
                        </SheetTrigger>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
